import { HighlightedText } from "@/components/highlighted-text"
import { CommandItem } from "@/components/ui/command"
import { joinStrings } from "@/lib/joinString"
import { MedicationSearchResultsType } from "@/modules/patients/detail/medication/de/search-results"
import { useDeMedicationSearchQuery$data } from "@/modules/patients/services/de/__generated__/useDeMedicationSearchQuery.graphql"

type DePreparationResult = NonNullable<useDeMedicationSearchQuery$data["de_preparations"]["result"]>
type DeSubstanceResult = NonNullable<useDeMedicationSearchQuery$data["de_substances"]["result"]>

type MedicationSearchResultsProps = {
  searchValue: string
  preparations: DePreparationResult
  substances: DeSubstanceResult
  handleSelectSearchItem: (selectedMedication: MedicationSearchResultsType) => void
}

const MedicationSearchResults = ({
  searchValue,
  preparations,
  substances,
  handleSelectSearchItem,
}: MedicationSearchResultsProps) => {
  return (
    <div className="px-2 pt-2.5" role="group">
      {substances.map((substance) => (
        <CommandItem
          key={substance.substanceId}
          className="cursor-pointer rounded-md"
          onSelect={() => handleSelectSearchItem({ substance })}
          value={substance.substanceId.toString()}
          data-testid="medication-search-item">
          <div className="grid">
            <HighlightedText
              className="text-on-surface truncate text-sm"
              title={substance.nameHtml}
              text={substance.nameHtml}
              searchValue={searchValue}
              extendedMarkClasses="font-normal"
            />
          </div>
        </CommandItem>
      ))}

      {preparations.map((preparation) => {
        const formattedName = joinStrings([preparation.name, ...preparation.composition])
        const pharmaceuticalName = preparation.pharmaceuticalForm?.name?.trim()

        return (
          <CommandItem
            key={preparation.productId}
            className="cursor-pointer rounded-md"
            value={preparation.productId.toString()}
            onSelect={() => handleSelectSearchItem({ preparation })}
            data-testid="medication-search-item">
            <div className="grid">
              <HighlightedText
                className="text-on-surface truncate text-sm"
                title={formattedName}
                text={formattedName}
                searchValue={searchValue}
                extendedMarkClasses="font-normal"
              />
              {pharmaceuticalName && <span className="text-on-surface-subdued text-xs">{pharmaceuticalName}</span>}
              {preparation.pharmaceuticalCentralNumberList?.map((pharma) => {
                const formattedPreparationText = `${pharma.unit} (${pharma.quantity}) ${pharma.price}`
                return (
                  <span key={pharma.pharmaceuticalCentralNumber} className="text-on-surface-subdued text-xs">
                    {formattedPreparationText}
                  </span>
                )
              })}
            </div>
          </CommandItem>
        )
      })}
    </div>
  )
}

export { MedicationSearchResults }
